import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccountsTable = ({ accounts, onAccountClick, onPaymentClick }) => {
  const [sortField, setSortField] = useState('balance');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'paid':
        return 'bg-success/10 text-success border-success';
      case 'denied':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredAccounts = accounts?.filter(account =>
    account?.patientName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    account?.accountNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const sortedAccounts = [...filteredAccounts]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    
    if (sortField === 'balance' || sortField === 'insuranceBalance') {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Patient Accounts</h2>
          <div className="relative w-full sm:w-64">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 h-10 bg-muted border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('patientName')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Patient
                  <Icon name={sortField === 'patientName' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left hidden md:table-cell">
                <button
                  onClick={() => handleSort('accountNumber')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Account #
                  <Icon name={sortField === 'accountNumber' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('balance')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Balance
                  <Icon name={sortField === 'balance' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Insurance</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Status
                  <Icon name={sortField === 'status' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAccounts?.map((account) => (
              <tr key={account?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4">
                  <div>
                    <button
                      onClick={() => onAccountClick(account)}
                      className="font-medium text-foreground hover:text-primary transition-smooth text-left"
                    >
                      {account?.patientName}
                    </button>
                    <p className="text-xs text-muted-foreground mt-1">DOB: {account?.dob}</p>
                  </div>
                </td>
                <td className="px-4 py-4 hidden md:table-cell">
                  <span className="font-mono text-sm text-foreground">{account?.accountNumber}</span>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-semibold text-foreground">${account?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-muted-foreground mt-1">Ins: ${account?.insuranceBalance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                </td>
                <td className="px-4 py-4 hidden lg:table-cell">
                  <div>
                    <p className="text-sm font-medium text-foreground">{account?.insuranceProvider}</p>
                    <p className="text-xs text-muted-foreground mt-1">{account?.insuranceId}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(account?.status)}`}>
                    {account?.status === 'overdue' && <Icon name="AlertCircle" size={12} />}
                    {account?.status === 'denied' && <Icon name="XCircle" size={12} />}
                    {account?.status === 'paid' && <Icon name="CheckCircle" size={12} />}
                    {account?.status?.charAt(0)?.toUpperCase() + account?.status?.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="DollarSign"
                      onClick={() => onPaymentClick(account)}
                      className="hidden sm:flex"
                    >
                      Payment
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="MoreVertical"
                      onClick={() => onAccountClick(account)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedAccounts?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-foreground font-medium mb-1">No accounts found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your search criteria</p>
        </div>
      )}
    </div>
  );
};

export default AccountsTable;